import { AuthPermission } from '@bechtel/sr-types';
import { useAppUser } from '~/components';

export function usePermissions() {
  const { hasPermission } = useAppUser();
  // Metadata tablename.columnname. Exaple: CABLE_CODE.GLAND
  const hasFieldPermission = (fieldName: string) => hasPermission(fieldName);
  const permissions = {
    // Equipment
    canAddEquipment: hasPermission(AuthPermission.ADD_EQUIPMENT),
    canUpdateEquipment: hasPermission(AuthPermission.CHANGE_EQUIPMENT),
    canDeleteEquipment: hasPermission(AuthPermission.DELETE_EQUIPMENT),
    canCheckEquipment: hasPermission(AuthPermission.CHECK_EQUIPMENT),
    canApproveEquipment: hasPermission(AuthPermission.APPROVE_EQUIPMENT),
    canPrepareEquipment: hasPermission(AuthPermission.PREPARE_EQUIPMENT),
    canIssueEquipment: hasPermission(AuthPermission.ISSUE_EQUIPMENT),
    canAddEquipmentConstructionNote: hasPermission(AuthPermission.SET_EQUIPMENT_CONSTRUCTION_NOTE),
    canAddEquipmentEngineeringNote: hasPermission(AuthPermission.SET_EQUIPMENT_NOTE),
    // Equipment code
    canAddEquipmentCode: hasPermission(AuthPermission.ADD_EQUIPMENT_CODE),
    canUpdateEquipmentCode: hasPermission(AuthPermission.CHANGE_EQUIPMENT_CODE),
    canCheckEquipmentCode: hasPermission(AuthPermission.CHECK_EQUIPMENT_CODE),
    canApproveEquipmentCode: hasPermission(AuthPermission.APPROVE_EQUIPMENT_CODE),
    canPrepareEquipmentCode: hasPermission(AuthPermission.PREPARE_EQUIPMENT_CODE),
    canIssueEquipmentCode: hasPermission(AuthPermission.ISSUE_EQUIPMENT_CODE),
    canDeleteEquipmentCode: hasPermission(AuthPermission.DELETE_EQUIPMENT_CODE),
    // Admin
    // isSystemAdmin: hasPermission(AuthPermission.SYSTEM_ADMINISTRATOR),
    // Color code & fire area
    canAddColorCode: hasPermission(AuthPermission.ADD_COLOR_CODE),
    canUpdateColorCode: hasPermission(AuthPermission.CHANGE_COLOR_CODE),
    canDeleteColorCode: hasPermission(AuthPermission.DELETE_COLOR_CODE),
    canSetFireArea: hasPermission(AuthPermission.SET_FIRE_AREA),
    canCheckFireArea: hasPermission(AuthPermission.CHECK_FIRE_AREA),
    canApproveFireArea: hasPermission(AuthPermission.APPROVE_FIRE_AREA),
    canPrepareFireArea: hasPermission(AuthPermission.PREPARE_FIRE_AREA),
    canIssueFireArea: hasPermission(AuthPermission.ISSUE_FIRE_AREA),
    // Cable code
    canAddCableCode: hasPermission(AuthPermission.ADD_CABLE_CODE),
    canUpdateCableCode: hasPermission(AuthPermission.CHANGE_CABLE_CODE),
    canDeleteCableCode: hasPermission(AuthPermission.DELETE_CABLE_CODE),
    canCheckCableCode: hasPermission(AuthPermission.CHECK_CABLE_CODE),
    canApproveCableCode: hasPermission(AuthPermission.APPROVE_CABLE_CODE),
    canPrepareCableCode: hasPermission(AuthPermission.PREPARE_CABLE_CODE),
    canIssueCableCode: hasPermission(AuthPermission.ISSUE_CABLE_CODE),
    canChangeAdminCableCode: hasPermission(AuthPermission.CHANGE_ADMIN_CABLE_CODE),
    // Raceway code
    canAddRacewayCode: hasPermission(AuthPermission.ADD_RACEWAY_CODE),
    canUpdateRacewayCode: hasPermission(AuthPermission.CHANGE_RACEWAY_CODE),
    canDeleteRacewayCode: hasPermission(AuthPermission.DELETE_RACEWAY_CODE),
    canCheckRacewayCode: hasPermission(AuthPermission.CHECK_RACEWAY_CODE),
    canApproveRacewayCode: hasPermission(AuthPermission.APPROVE_RACEWAY_CODE),
    canPrepareRacewayCode: hasPermission(AuthPermission.PREPARE_RACEWAY_CODE),
    canIssueRacewayCode: hasPermission(AuthPermission.ISSUE_RACEWAY_CODE),
    canChangeAdminRacewayCode: hasPermission(AuthPermission.CHANGE_ADMIN_RACEWAY_CODE),
    // Project
    isSystemAdmin: hasPermission(AuthPermission.SYSTEM_ADMINISTRATOR),
    canCheckConfiguration: hasPermission(AuthPermission.CHECK_CONFIGURATION),
    canApproveConfiguration: hasPermission(AuthPermission.APPROVE_CONFIGURATION),
    canPrepareConfiguration: hasPermission(AuthPermission.PREPARE_CONFIGURATION),
    canIssueConfiguration: hasPermission(AuthPermission.ISSUE_CONFIGURATION),
    // Raceway
    canAddRaceway: hasPermission(AuthPermission.ADD_RACEWAY),
    canUpdateRaceway: hasPermission(AuthPermission.CHANGE_RACEWAY),
    canDeleteRaceway: hasPermission(AuthPermission.DELETE_RACEWAY),
    canCheckRaceway: hasPermission(AuthPermission.CHECK_RACEWAY),
    canApproveRaceway: hasPermission(AuthPermission.APPROVE_RACEWAY),
    canPrepareRaceway: hasPermission(AuthPermission.PREPARE_RACEWAY),
    canIssueRaceway: hasPermission(AuthPermission.ISSUE_RACEWAY),
    canAddRacewayWrap: hasPermission(AuthPermission.SET_WRAP),
    canSetRacewayBarrier: hasPermission(AuthPermission.SET_RACEWAY_BARRIER),
    canAddRacewayConstructionNote: hasPermission(AuthPermission.SET_RACEWAY_CONSTRUCTION_NOTE),
    canAddRacewayEngineeringNote: hasPermission(AuthPermission.SET_RACEWAY_NOTE),
    // Feedback
    canUpdateJumperFeedback: hasPermission(AuthPermission.SET_JUMPER_FEEDBACK),
    canUpdateRacewayFeedback: hasPermission(AuthPermission.SET_RACEWAY_FEEDBACK),
    canUpdateEquipmentFeedback: hasPermission(AuthPermission.SET_EQUIPMENT_FEEDBACK),
    canUpdateCableFeedback: hasPermission(AuthPermission.SET_CABLE_FEEDBACK),
    canUpdateTerminationFeedback: hasPermission(AuthPermission.SET_TERMINATION_FEEDBACK),
    // Jumper
    canAddJumper: hasPermission(AuthPermission.ADD_JUMPER),
    canUpdateJumper: hasPermission(AuthPermission.CHANGE_JUMPER),
    canCheckJumper: hasPermission(AuthPermission.CHECK_JUMPER),
    canApproveJumper: hasPermission(AuthPermission.APPROVE_JUMPER),
    canPrepareJumper: hasPermission(AuthPermission.PREPARE_JUMPER),
    canIssueJumper: hasPermission(AuthPermission.ISSUE_JUMPER),
    canDeleteJumper: hasPermission(AuthPermission.DELETE_JUMPER),
    canAddJumperConstructionNote: hasPermission(AuthPermission.SET_JUMPER_CONSTRUCTION_NOTE),
    canAddJumperEngineeringNote: hasPermission(AuthPermission.SET_JUMPER_NOTE),
    // Reel
    canSetReel: hasPermission(AuthPermission.SET_REEL),
    // Cable
    canAddCable: hasPermission(AuthPermission.ADD_CABLE),
    canUpdateCable: hasPermission(AuthPermission.CHANGE_CABLE),
    canDeleteCable: hasPermission(AuthPermission.DELETE_CABLE),
    canCheckCable: hasPermission(AuthPermission.CHECK_CABLE),
    canApproveCable: hasPermission(AuthPermission.APPROVE_CABLE),
    canPrepareCable: hasPermission(AuthPermission.PREPARE_CABLE),
    canIssueCable: hasPermission(AuthPermission.ISSUE_CABLE),
    canSetCableReel: hasPermission(AuthPermission.SET_CABLE_REEL),
    canAddCableConstructionNote: hasPermission(AuthPermission.SET_CABLE_CONSTRUCTION_NOTE),
    canAddCableEngineeringNote: hasPermission(AuthPermission.SET_CABLE_NOTE),
    // Fittings
    canSetFitting: hasPermission(AuthPermission.SET_FITTING),
    //Routings
    canCheckRoute: hasPermission(AuthPermission.CHECK_ROUTE),
    canApproveRoute: hasPermission(AuthPermission.APPROVE_ROUTE),
    canPrepareRoute: hasPermission(AuthPermission.PREPARE_ROUTE),
    canIssueRoute: hasPermission(AuthPermission.ISSUE_ROUTE),
    // Terminations
    canCheckTerm: hasPermission(AuthPermission.CHECK_TERM),
    canApproveTerm: hasPermission(AuthPermission.APPROVE_TERM),
    canPrepareTerm: hasPermission(AuthPermission.PREPARE_TERM),
    canIssueTerm: hasPermission(AuthPermission.ISSUE_TERM),
    // Terms
    canSetTerm: hasPermission(AuthPermission.SET_TERM),
    // Package
    canSetPackage: hasPermission(AuthPermission.SET_PACKAGE),
    canUpdatePackage: hasPermission(AuthPermission.SET_PACKAGE),
    // System
    canAddDeleteSystem: hasPermission('SYSTEM.TAG'),
    // Startup System
    canAddDeleteStartupSystem: hasPermission('STARTUP_SYSTEM.TAG'),
    // override primary status
    canOverridePriStatAll: hasPermission(AuthPermission.OVERRIDE_PRISTAT_ALL),
    canOverridePriStatJtoR: hasPermission(AuthPermission.OVERRIDE_PRISTAT_JTOR_ONLY),
    // Authorize Fill
    canAuthorizeFill: hasPermission(AuthPermission.AUTHORIZE_FILL),
    // Insert Block
    canAddInsertBlock: hasPermission('INSERTBLOCK.TAG'),
    // Bulk permissions
    canPrepareBulk: hasPermission(AuthPermission.PREPARE_BULK),
    canCheckBulk: hasPermission(AuthPermission.CHECK_BULK),
    canApproveBulk: hasPermission(AuthPermission.APPROVE_BULK),
    canIssueBulk: hasPermission(AuthPermission.ISSUE_BULK),

    hasFieldPermission: hasFieldPermission,
  };

  return permissions;
}
